import type { ClientPositionView } from "@solomon-apps/shared";
import {
    fetchUsdvBalanceForAddress,
    USDV_BALANCE_GC_TIME_MS,
    USDV_BALANCE_STALE_TIME_MS,
    usdvBalanceQueryKey
} from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { useQueries, useQuery } from "@tanstack/react-query";

import { fetchClientProgramSurfaces } from "@/services/clientPositions";

import { formatObservedUsdv, isLpPosition, lpPositionAmount, observedUsdv } from "./positionAmounts";
import { normalizeProgramSurfaceType, shouldFetchLiveUsdvBalance } from "./positionDisplayAmount";
import { useClientContextKey } from "./useClientContextKey";

export interface PositionBalance {
    displayAmount: string;
    amount: BigDecimal | null;
    isLoading: boolean;
    error: Error | null;
    observedAt: string | null;
    shouldFetchLiveUsdv: boolean;
}

/** Shared resolver used by inventory, detail, dashboard and portfolio totals.
 * LP observations come from the context-scoped client-positions query; never query
 * the personal-position account as a wallet or use its manual amount. */
export function usePositionBalances(positions: ClientPositionView[]): Map<string, PositionBalance> {
    const needsCatalog = positions.some((p) => normalizeProgramSurfaceType(p.programSurface.type) === null);
    const contextKey = useClientContextKey();
    const { data: surfaces, isLoading: catalogLoading } = useQuery({
        queryKey: ["client-program-surfaces", contextKey],
        queryFn: fetchClientProgramSurfaces,
        enabled: needsCatalog,
        staleTime: 5 * 60 * 1000
    });
    const isDirect = (p: ClientPositionView): boolean =>
        !isLpPosition(p) &&
        shouldFetchLiveUsdvBalance(
            p.programSurface.type,
            surfaces?.find((s) => s.uuid === p.programSurface.uuid)?.type,
            p.programSurface.surface
        );
    const directPositions = positions.filter(isDirect);
    const directAddresses = [...new Set(directPositions.map((p) => p.usdvBalanceAddress ?? p.account.address))];
    const queries = useQueries({
        queries: directAddresses.map((address) => {
            return {
                queryKey: ["client-position-balance", contextKey, ...usdvBalanceQueryKey(address)],
                queryFn: (): Promise<BigDecimal | null> => fetchUsdvBalanceForAddress(address),
                staleTime: USDV_BALANCE_STALE_TIME_MS,
                gcTime: USDV_BALANCE_GC_TIME_MS,
                refetchInterval: USDV_BALANCE_STALE_TIME_MS,
                refetchOnWindowFocus: "always"
            };
        })
    });
    const byAddress = new Map(directAddresses.map((address, i) => [address, queries[i]]));
    return new Map(
        positions.map((position) => {
            const direct = isDirect(position);
            const query = direct ? byAddress.get(position.usdvBalanceAddress ?? position.account.address) : undefined;
            const loading =
                Boolean(query?.isPending) ||
                (needsCatalog && catalogLoading && normalizeProgramSurfaceType(position.programSurface.type) === null);
            const error = query?.error instanceof Error ? query.error : null;
            const amount = isLpPosition(position)
                ? lpPositionAmount(position)
                : direct
                  ? error || loading
                      ? null
                      : (query?.data ?? new BigDecimal(0))
                  : observedUsdv(position.manualUsdvAmount);
            return [
                position.uuid,
                {
                    displayAmount: loading ? "..." : formatObservedUsdv(amount),
                    amount,
                    isLoading: loading,
                    error,
                    observedAt: position.observation?.observedAt ?? position.observedAt ?? null,
                    shouldFetchLiveUsdv: direct
                }
            ];
        })
    );
}
